import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Feed from "@/pages/Feed";
import Courses from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import CourseUpload from "@/pages/CourseUpload";
import Build from "@/pages/Build";
import BuildProject from "@/pages/BuildProject";
import Events from "@/pages/Events";
import Leaderboard from "@/pages/Leaderboard";
import Members from "@/pages/Members";
import Profile from "@/pages/Profile";
import Onboarding from "@/pages/Onboarding";
import Matches from "@/pages/Matches";
import SpacesBrowse from "@/pages/SpacesBrowse";
import Messages from "@/pages/Messages";
import Upgrade from "@/pages/Upgrade";
import Admin from "@/pages/Admin";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import "@/App.css";

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background dot-grid">
        <div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  if (adminOnly && !user.is_admin) return <Navigate to="/feed" replace />;
  return <Layout>{children}</Layout>;
}

function OnboardingRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background dot-grid">
        <div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return <Onboarding />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/feed" element={<Protected><Feed /></Protected>} />
      <Route path="/onboarding" element={<OnboardingRoute />} />
      <Route path="/matches" element={<Protected><Matches /></Protected>} />
      <Route path="/spaces" element={<Protected><SpacesBrowse /></Protected>} />
      <Route path="/messages" element={<Protected><Messages /></Protected>} />
      <Route path="/messages/:conversationId" element={<Protected><Messages /></Protected>} />
      <Route path="/upgrade" element={<Protected><Upgrade /></Protected>} />
      <Route path="/space/:slug" element={<Protected><Feed /></Protected>} />
      <Route path="/courses" element={<Protected><Courses /></Protected>} />
      <Route path="/courses/new" element={<Protected><CourseUpload /></Protected>} />
      <Route path="/courses/:courseId/edit" element={<Protected><CourseUpload /></Protected>} />
      <Route path="/courses/:courseId" element={<Protected><CourseDetail /></Protected>} />
      <Route path="/build" element={<Protected><Build /></Protected>} />
      <Route path="/build/:projectId" element={<Protected><BuildProject /></Protected>} />
      <Route path="/events" element={<Protected><Events /></Protected>} />
      <Route path="/leaderboard" element={<Protected><Leaderboard /></Protected>} />
      <Route path="/members" element={<Protected><Members /></Protected>} />
      <Route path="/members/:userId" element={<Protected><Profile /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" toastOptions={{ style: { fontFamily: "Inter" } }} />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
